import {Injectable} from '@angular/core';
import {translate, TranslocoService} from '@ngneat/transloco';
import {ConfirmationService, MessageService} from 'primeng/api';
import {HttpErrorResponse, HttpStatusCode} from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class CommonMessagesService {

  constructor(private i18n: TranslocoService,
              private confirmationService: ConfirmationService,
              private messageService: MessageService) {
  }

  public showSuccessfullyUploadedMessage(): void {
    const summary = this.i18n.translate('success.generic.changesApplied');
    const detail = this.i18n.translate('success.generic.uploaded');
    this.messageService.add({severity: 'success', summary, detail, sticky: false});
  }

  public showSuccessfullyDeletedMessage(): void {
    const summary = this.i18n.translate('success.generic.changesApplied');
    const detail = this.i18n.translate('success.generic.deleted');
    this.messageService.add({severity: 'success', summary, detail, sticky: false});
  }

  public showSuccessfullyStopSharingMessage(): void {
    const summary = this.i18n.translate('success.generic.changesApplied');
    const detail = this.i18n.translate('success.generic.stopSharing');
    this.messageService.add({severity: 'success', summary, detail, sticky: true});
  }

  public showCopiedToClipboardMessage(): void {
    const summary = this.i18n.translate('success.generic.clipboard');
    const detail = this.i18n.translate('success.copy.clipboard');
    this.messageService.add({severity: 'success', summary, detail, sticky: false});
  }

  public showRestoreConfigurationMessage(): void {
    const summary = this.i18n.translate('success.generic.restore');
    const detail = this.i18n.translate('success.restore.configuration');
    this.messageService.add({severity: 'success', summary, detail, sticky: false});
  }

  public showPersistingError(mode: 'create' | 'edit', error: HttpErrorResponse, identifier: string): void {
    if (error.status === HttpStatusCode.Conflict) {
      this.showEntityAlreadyExistsError(mode, identifier);
    } else {
      this.showUnexpectedPersistingError(mode);
    }
  }

  public showEntityAlreadyExistsError(mode: 'create' | 'edit', identifier: string): void {
    const summary = this.i18n.translate('error.' + mode + '.generic');
    const detail = this.i18n.translate('error.persist.alreadyExists', {identifier});
    this.messageService.add({severity: 'error', summary, detail, sticky: true});
  }

  public showUnexpectedPersistingError(mode: 'create' | 'edit'): void {
    const summary = this.i18n.translate('error.summary.unexpected');
    const detail = this.i18n.translate('error.' + mode + '.unexpected');
    this.messageService.add({severity: 'error', summary, detail, sticky: true});
  }

  public showErrorWhileDeletingMessage(httpError: number, multiple: boolean = false): void {
    let summary;
    let detail;

    if (httpError === HttpStatusCode.Conflict) {
      summary = this.i18n.translate('error.summary.restricted');
      detail = this.i18n.translate(multiple ? 'error.delete.restrictedMultiple' : 'error.delete.restricted');
    } else if (httpError === HttpStatusCode.NotFound) {
      summary = this.i18n.translate('error.summary.notFound');
      detail = this.i18n.translate('error.delete.notFound');
    } else {
      summary = this.i18n.translate('error.summary.unexpected');
      detail = this.i18n.translate('error.delete.unexpected');
    }

    this.messageService.add({severity: 'error', summary, detail, sticky: true});
  }

  public showErrorWhileSharingMessage(): void {
    let summary = this.i18n.translate('error.summary.unexpected');
    let detail = this.i18n.translate('error.share.unexpected');
    this.messageService.add({severity: 'error', summary, detail, sticky: true});
  }

  public showDeleteRequestConfirmation(acceptCallback, multiple: boolean = false): void {
    this.confirmationService.confirm({
      icon: 'pi pi-exclamation-triangle',
      message: this.i18n.translate(multiple ? 'info.confirmDeleteMultiple' : 'info.confirmDelete'),
      header: this.i18n.translate('forms.confirmation'),
      acceptLabel: this.i18n.translate('common.accept'),
      rejectLabel: this.i18n.translate('common.cancel'),
      acceptIcon: 'pi pi-check',
      defaultFocus: 'none',
      acceptButtonStyleClass: 'p-button-danger p-button-text',
      accept: acceptCallback
    });
  }

  public showStopSharingRequestConfirmation(acceptCallback, multiple: boolean = false): void {
    this.confirmationService.confirm({
      icon: 'pi pi-exclamation-triangle',
      message: this.i18n.translate(multiple ? 'info.confirmStopSharingMultiple' : 'info.confirmStopSharing'),
      header: this.i18n.translate('forms.confirmation'),
      acceptLabel: this.i18n.translate('common.accept'),
      rejectLabel: this.i18n.translate('common.cancel'),
      acceptIcon: 'pi pi-check',
      defaultFocus: 'none',
      acceptButtonStyleClass: 'p-button-danger p-button-text',
      accept: acceptCallback
    });
  }

  public showCancelRequestConfirmation(acceptCallback, rejectCallback?): void {
    this.confirmationService.confirm({
      icon: 'pi pi-exclamation-triangle',
      message: this.i18n.translate('info.confirmCancel'),
      header: this.i18n.translate('forms.confirmation'),
      acceptLabel: this.i18n.translate('common.accept'),
      rejectLabel: this.i18n.translate('common.cancel'),
      acceptIcon: 'pi pi-check',
      defaultFocus: 'none',
      acceptButtonStyleClass: 'p-button-danger p-button-text',
      accept: acceptCallback,
      reject: rejectCallback
    });
  }

  public showDeleteSimpleRulesConfirmation(acceptCallback, rejectCallback?): void {
    this.confirmationService.confirm({
      icon: 'pi pi-exclamation-triangle',
      message: this.i18n.translate('info.confirmDeleteSimpleRules'),
      header: this.i18n.translate('forms.confirmation'),
      acceptLabel: this.i18n.translate('common.accept'),
      rejectLabel: this.i18n.translate('common.cancel'),
      acceptIcon: 'pi pi-check',
      defaultFocus: 'none',
      acceptButtonStyleClass: 'p-button-danger p-button-text',
      accept: acceptCallback,
      reject: rejectCallback
    });
  }

  public showDeleteEntitiesSelectedConfirmation(acceptCallback, rejectCallback?): void {
    this.confirmationService.confirm({
      icon: 'pi pi-exclamation-triangle',
      message: this.i18n.translate('info.confirmDeleteEntitiesSelected'),
      header: this.i18n.translate('forms.confirmation'),
      acceptLabel: this.i18n.translate('common.accept'),
      rejectLabel: this.i18n.translate('common.cancel'),
      acceptIcon: 'pi pi-check',
      defaultFocus: 'none',
      acceptButtonStyleClass: 'p-button-danger p-button-text',
      accept: acceptCallback,
      reject: rejectCallback
    });
  }

  public showCreateKpiConfirmation(acceptCallback, rejectCallback, totalData): void {
    const content = `
    <div>
      <p>${translate('misc.kpiCreationInfo1')}:</p>
      <ul>
        <li>${translate('model.deviceMeasurements')}: ${totalData.filteredDeviceMeasurements}</li>
        <li>${translate('model.assetMeasurements')}: ${totalData.filteredAssetMeasurements}</li>
        <li>${translate('model.kpis')}: ${totalData.filteredKpis}</li>
      </ul>
      ${totalData.deviceMeasurements + totalData.assetMeasurements + totalData.kpis > 0 ? `
      <p>${translate('misc.kpiCreationInfo2')}:</p>
      <ul>
        <li>${translate('model.deviceMeasurements')}: ${totalData.deviceMeasurements}</li>
        <li>${translate('model.assetMeasurements')}: ${totalData.assetMeasurements}</li>
        <li>${translate('model.kpis')}: ${totalData.kpis}</li>
      </ul>` : ''}
    </div>
    `;
    this.confirmationService.confirm({
      message: content,
      header: this.i18n.translate('forms.confirmation'),
      acceptLabel: this.i18n.translate('common.create'),
      rejectLabel: this.i18n.translate('common.cancel'),
      acceptIcon: 'pi pi-check',
      defaultFocus: 'none',
      rejectButtonStyleClass: 'p-button-text',
      accept: acceptCallback,
      reject: rejectCallback
    });
  }

  public showImportCsvConfirmation(acceptCallback): void {
    this.confirmationService.confirm({
      icon: 'pi pi-exclamation-triangle',
      message: this.i18n.translate('info.confirmImportCsvFiles'),
      header: this.i18n.translate('forms.confirmation'),
      acceptLabel: this.i18n.translate('common.accept'),
      rejectLabel: this.i18n.translate('common.cancel'),
      acceptIcon: 'pi pi-check',
      defaultFocus: 'none',
      acceptButtonStyleClass: 'p-button-danger p-button-text',
      accept: acceptCallback
    });
  }

  public showGenerateNewApiKeyConfirmation(acceptCallback, rejectCallback?): void {
    this.confirmationService.confirm({
      icon: 'pi pi-exclamation-triangle',
      message: this.i18n.translate('info.confirmNewApiKey'),
      header: this.i18n.translate('forms.confirmation'),
      acceptLabel: this.i18n.translate('common.accept'),
      rejectLabel: this.i18n.translate('common.cancel'),
      acceptIcon: 'pi pi-check',
      defaultFocus: 'none',
      acceptButtonStyleClass: 'p-button-danger p-button-text',
      accept: acceptCallback,
      reject: rejectCallback
    });
  }

  public showRestartSequenceConfirmation(acceptCallback: any, sequence: string): void {
    this.confirmationService.confirm({
      icon: 'pi pi-exclamation-triangle',
      message: this.i18n.translate('info.confirmRestartSequence', {sequence}),
      header: this.i18n.translate('forms.confirmation'),
      acceptLabel: this.i18n.translate('common.accept'),
      rejectLabel: this.i18n.translate('common.cancel'),
      acceptIcon: 'pi pi-check',
      defaultFocus: 'none',
      acceptButtonStyleClass: 'p-button-danger p-button-text',
      accept: acceptCallback
    });
  }

  public showSequenceExecuteConfirmation(acceptCallback: any, sequence: string): void {
    const message = this.i18n.translate('info.confirmExecuteSequence', {sequence});
    this.confirmationService.confirm({
      icon: 'pi pi-exclamation-triangle',
      message,
      header: this.i18n.translate('forms.confirmation'),
      acceptLabel: this.i18n.translate('common.accept'),
      rejectLabel: this.i18n.translate('common.cancel'),
      acceptIcon: 'pi pi-check',
      defaultFocus: 'none',
      acceptButtonStyleClass: 'p-button-danger p-button-text',
      accept: acceptCallback
    });
  }

  public showSuccessfullyUploadMeasurementsMessage(): void {
    const summary = this.i18n.translate('success.generic.changesApplied');
    const detail = this.i18n.translate('success.generic.inserted');
    this.messageService.add({severity: 'success', summary, detail, sticky: false});
  }

  public showErrorUploadMeasurementsMessage(): void {
    let summary = this.i18n.translate('error.summary.invalidFormat');
    let detail = this.i18n.translate('error.importCsv.wrongFileFormat');
    this.messageService.add({severity: 'error', summary, detail, sticky: true});
  }

  public showErrorUploadSequenceConfigurationMessage(): void {
    let summary = this.i18n.translate('error.summary.invalidFormat');
    let detail = this.i18n.translate('error.importJson.wrongFileFormat');
    this.messageService.add({severity: 'error', summary, detail, sticky: true});
  }

  public showErrorUploadMultipleFilesWhenUniqueFileMessage(): void {
    let summary = this.i18n.translate('error.summary.unexpected');
    let detail = this.i18n.translate('error.files.unique');
    this.messageService.add({severity: 'error', summary, detail, sticky: true});
  }

  public showSequenceErrorConfigurationInvalidMessage(mode: 'create' | 'edit'): void {
    const summary = this.i18n.translate('error.' + mode + '.generic');
    const detail = this.i18n.translate('error.persist.sequenceConfiguration');
    this.messageService.add({severity: 'error', summary, detail, sticky: true});
  }

  public showSuccessfullyUpdatedMessage(): void {
    const summary = this.i18n.translate('success.generic.changesApplied');
    const detail = this.i18n.translate('success.generic.update');
    this.messageService.add({severity: 'success', summary, detail, sticky: false});
  }

}
